import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box2, Camera, Group, Raycaster, Vector2, Vector3 } from 'three';
import { Plane } from 'three';

import { ThreeEvent, useThree } from '@react-three/fiber';
import {
  getClosestPointOnNavMesh,
  isPointInNavMesh,
  NavMeshQuery,
} from '@spazious/dollhouse-navmesh';
import { useForceUpdate } from '@spazious/react-hooks';

import { Polygon } from '../Polygon/index.js';

export const minDistance = (source: Vector3, target: Vector3, minDistance: number) => {
  const direction = new Vector3().subVectors(target, source).normalize();
  const distance = Math.max(minDistance, source.distanceTo(target));
  return direction.multiplyScalar(distance).add(source).toArray();
};

const fromPointer = (
  raycaster: Raycaster,
  pointer: Vector2,
  plane: Plane,
  camera: Camera,
  target: Vector3 = new Vector3()
) => {
  raycaster.setFromCamera(pointer, camera);
  raycaster.ray.intersectPlane(plane, target);
  return target;
};

const getPointsFromBox2 = (box: Box2) => {
  const center = box.getCenter(new Vector2());
  const size = box.getSize(new Vector2());

  return [
    new Vector2(center.x - size.x / 2, center.y - size.y / 2),
    new Vector2(center.x + size.x / 2, center.y - size.y / 2),
    new Vector2(center.x + size.x / 2, center.y + size.y / 2),
    new Vector2(center.x - size.x / 2, center.y + size.y / 2),
    new Vector2(center.x - size.x / 2, center.y - size.y / 2),
  ];
};

export interface DragEvent {
  originalEvent: ThreeEvent<PointerEvent> | PointerEvent;
  center: [number, number];
  size: [number, number];
}

export interface RectDrawControlsProps {
  fromCenter?: boolean;
  onDraw?: (evt: DragEvent) => void;
  onDrawEnd?: (evt: DragEvent) => void;
  color?: string;
  navMeshQuery?: NavMeshQuery;
}

const makeRect = (box: Box2, a: Vector2, b: Vector2, fromCenter?: boolean) => {
  return fromCenter
    ? box.setFromCenterAndSize(
        a,
        new Vector2().subVectors(a.clone().max(b), a.clone().min(b)).multiplyScalar(2)
      )
    : box.setFromPoints([a, b]);
};

const _box = new Box2();
const _va = new Vector3();
const _vb = new Vector3();

/**
 *
 */
export const RectDrawControls = forwardRef<Group, RectDrawControlsProps>(
  ({ fromCenter, onDraw, onDrawEnd, color = 'red', navMeshQuery }) => {
    const controls = useThree(({ controls }) => controls);
    const events = useThree(({ events }) => events);
    const pointer = useThree(({ pointer }) => pointer);
    const camera = useThree(({ camera }) => camera);
    const raycaster = useThree(({ raycaster }) => raycaster);
    const forceUpdate = useForceUpdate();
    const dragging = useRef(false);

    const minSize = 2;

    const plane = useMemo(() => new Plane(camera.up.clone(), 0), [camera.up]);

    const updatePosition = useCallback(
      (target: Vector3) => {
        return fromPointer(raycaster, pointer, plane, camera, target);
      },
      [camera, plane, pointer, raycaster]
    );

    const computeBox = useCallback(
      () => makeRect(_box, new Vector2(_va.x, _va.z), new Vector2(_vb.x, _vb.z), fromCenter),
      [fromCenter]
    );

    const handlerMove = useCallback(
      (evt: PointerEvent) => {
        if (!dragging.current) {
          return;
        }
        updatePosition(_vb);
        getClosestPointOnNavMesh(_vb, _vb, navMeshQuery);
        minDistance(_va, _vb, minSize);
        computeBox();
        onDraw?.({
          originalEvent: evt,
          center: _box.getCenter(new Vector2()).toArray(),
          size: _box.getSize(new Vector2()).toArray(),
        });
        forceUpdate();
      },
      [computeBox, forceUpdate, navMeshQuery, onDraw, updatePosition]
    );

    const handlerDown = useCallback(
      (evt: PointerEvent) => {
        updatePosition(_va).toArray();
        _vb.copy(_va);
        const pointInNavmesh = isPointInNavMesh(_va, navMeshQuery);
        if (pointInNavmesh) {
          onDraw?.({
            originalEvent: evt,
            center: _box.getCenter(new Vector2()).toArray(),
            size: _box.getSize(new Vector2()).toArray(),
          });
        }
        computeBox();
        dragging.current = pointInNavmesh;
        (controls as any).enabled = !pointInNavmesh;

        forceUpdate();
      },
      [computeBox, controls, forceUpdate, navMeshQuery, onDraw, updatePosition]
    );

    const handlerUp = useCallback(
      (evt: PointerEvent) => {
        minDistance(_va, updatePosition(_vb), minSize);
        computeBox();
        const event = {
          originalEvent: evt,
          center: _box.getCenter(new Vector2()).toArray(),
          size: _box.getSize(new Vector2()).toArray(),
        };
        onDraw?.(event);
        onDrawEnd?.(event);
        dragging.current = false;
        (controls as any).enabled = true;
        forceUpdate();
      },
      [computeBox, controls, forceUpdate, onDraw, onDrawEnd, updatePosition]
    );

    const attachEvents = useCallback(() => {
      events.connected?.addEventListener('pointermove', handlerMove);
      events.connected?.addEventListener('pointerdown', handlerDown);
      events.connected?.addEventListener('pointerup', handlerUp);
    }, [events.connected, handlerDown, handlerMove, handlerUp]);

    const detachEvents = useCallback(() => {
      events.connected?.removeEventListener('pointermove', handlerMove);
      events.connected?.removeEventListener('pointerdown', handlerDown);
      events.connected?.removeEventListener('pointerup', handlerUp);
    }, [events.connected, handlerDown, handlerMove, handlerUp]);

    useEffect(() => {
      attachEvents();
      return () => {
        detachEvents();
      };
    }, [attachEvents, detachEvents, events.connected]);

    useEffect(() => {
      if (controls) {
        (controls as any).enabled = !dragging.current;
      }

      return () => {
        if (controls) {
          (controls as any).enabled = true;
        }
      };
    }, [controls]);

    if (!dragging.current) return null;

    return (
      <group>
        <Polygon color={color} lineWidth={2} points={getPointsFromBox2(_box)} />
      </group>
    );
  }
);

export default RectDrawControls;
